import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUp, ArrowDown, Camera, X, Pencil, Trash2 } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import UnitFormSlider from '../components/UnitFormSlider';
import UnitDetailsSlider from '../components/UnitDetailsSlider';
import UnitImportModal from '../components/UnitImportModal';
import { unitService } from '../services/unitService';
import { Unit, UnitFilters } from '../types/unit';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
  builderId: string;
}

const Units: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [units, setUnits] = useState<Unit[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<UnitFilters>({});
  const [uniqueFloors, setUniqueFloors] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<string>('unitNo');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Track previous values to determine what changed
  const prevFiltersRef = useRef<UnitFilters>(filters);
  const prevSearchRef = useRef(searchQuery);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editUnitId = searchParams.get('edit');
  const viewUnitId = searchParams.get('view');

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();

      // Check if filters or search changed
      const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
      const searchChanged = prevSearchRef.current !== searchQuery;

      // Update refs
      prevFiltersRef.current = filters;
      prevSearchRef.current = searchQuery;

      // Initial load or search/filter change - full loading
      if (units.length === 0 || searchChanged || filtersChanged) {
        fetchUnits(false);
      } else {
        // Data refresh (sort, pagination only) - only tbody loading
        fetchUnits(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, pagination.limit, searchQuery, filters, sortBy, sortOrder]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchUnits = async (isDataRefresh: boolean = false) => {
    if (!workspaceId) return;

    try {
      // Use dataLoading for data refreshes (sort, pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await unitService.getAll(workspaceId, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        sortBy: sortBy,
        sortOrder: sortOrder,
        ...filters
      });

      setUnits(response.units);
      setPagination(response.pagination);

      // Extract unique floor values
      const floors: number[] = response.units
        .map((unit: Unit) => unit.floorMarketing)
        .filter((floor: number | undefined): floor is number => floor !== undefined && floor !== null);
      const uniqueFloorsSet = Array.from(new Set<number>(floors)).sort((a, b) => a - b);
      setUniqueFloors(uniqueFloorsSet);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch units');
      console.error('Error fetching units:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key: keyof UnitFilters, value: any) => {
    setFilters({ ...filters, [key]: value || undefined });
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const handleCreateUnit = () => {
    setSearchParams({ action: 'new' });
  };

  const handleEditUnit = (unitId: string) => {
    setSearchParams({ edit: unitId });
  };

  const handleViewUnit = (unitId: string) => {
    setSearchParams({ view: unitId });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchUnits();
  };

  const handleCloseDetailsSlider = () => {
    setSearchParams({});
  };

  const handleDeleteClick = (unit: Unit) => {
    setUnitToDelete(unit);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!unitToDelete || !workspaceId) return;

    try {
      await unitService.delete(workspaceId, unitToDelete._id);
      setShowDeleteModal(false);
      setUnitToDelete(null);
      fetchUnits();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete unit');
      console.error('Error deleting unit:', err);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'badge-available';
      case 'RESERVED':
        return 'badge-reserved';
      case 'SOLD':
        return 'badge-sold';
      case 'UNAVAILABLE':
        return 'badge-unavailable';
      default:
        return '';
    }
  };

  const getDealStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'NEW':
        return 'badge-available';
      case 'RESERVED':
        return 'badge-reserved';
      case 'FIRM':
        return 'badge-reserved';
      case 'SOLD':
        return 'badge-sold';
      case 'CANCELLED':
        return 'badge-unavailable';
      default:
        return '';
    }
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Units</h2>
                <p className="builders-section-subtitle">
                  Manage unit inventory and information
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleCreateUnit}>
                  + Add New Unit
                </button>
                <button className="btn btn-primary btn-md" onClick={() => setShowImportModal(true)}>
                  Import
                </button>
              </div>
            </div>
          </div>

          {/* Card Container for Filters and Table */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
            <select
              className="form-input"
              value={filters.projectId || ''}
              onChange={(e) => handleFilterChange('projectId', e.target.value)}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="AVAILABLE">Available</option>
              <option value="RESERVED">Reserved</option>
              <option value="SOLD">Sold</option>
              <option value="UNAVAILABLE">Unavailable</option>
            </select>

            <select
              className="form-input"
              value={filters.bed || ''}
              onChange={(e) => handleFilterChange('bed', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Beds</option>
              <option value="0">Studio</option>
              <option value="1">1 Bed</option>
              <option value="2">2 Bed</option>
              <option value="3">3 Bed</option>
              <option value="4">4+ Bed</option>
            </select>

            <select
              className="form-input"
              value={filters.bath || ''}
              onChange={(e) => handleFilterChange('bath', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Baths</option>
              <option value="1">1 Bath</option>
              <option value="2">2 Bath</option>
              <option value="3">3 Bath</option>
              <option value="4">4+ Bath</option>
            </select>
          </div>

          {/* Second row of filters */}
          <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
            <select
              className="form-input"
              value={filters.floorMarketing || ''}
              onChange={(e) => handleFilterChange('floorMarketing', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Floor</option>
              {uniqueFloors.map((floor) => (
                <option key={floor} value={floor}>
                  Floor {floor}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.den || ''}
              onChange={(e) => handleFilterChange('den', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Den</option>
              <option value="0">No Den</option>
              <option value="1">1 Den</option>
              <option value="2">2 Den</option>
            </select>

            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                style={{ paddingRight: searchQuery ? '36px' : '12px', width: '100%' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

            {/* Error Display */}
            {error && (
              <div className="builders-error" style={{ marginBottom: '20px' }}>
                <p>{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="builders-container">
                <div className="builders-loading">
                  <div className="builders-loading-spinner" />
                </div>
              </div>
            ) : (
              <>
            {/* Units Table - Full Width */}
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div className="data-table-container" style={{ minWidth: '2000px', position: 'relative' }}>
                {dataLoading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <div className="contacts-loading-spinner"></div>
                  </div>
                )}
                <table className="data-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>
                      <button
                        onClick={() => handleSort('projectName')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Project
                        <span className="sort-icon">
                          {getSortIcon('projectName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('collection')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Model Collection
                        <span className="sort-icon">
                          {getSortIcon('collection')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('modelName')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Model Name
                        <span className="sort-icon">
                          {getSortIcon('modelName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('type')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Type
                        <span className="sort-icon">
                          {getSortIcon('type')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('unitNo')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Unit#
                        <span className="sort-icon">
                          {getSortIcon('unitNo')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('salesPrice')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Sale Price
                        <span className="sort-icon">
                          {getSortIcon('salesPrice')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('floorLegal')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Legal Floor
                        <span className="sort-icon">
                          {getSortIcon('floorLegal')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('floorMarketing')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Marketing Floor
                        <span className="sort-icon">
                          {getSortIcon('floorMarketing')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('unitNoLegal')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Legal Unit
                        <span className="sort-icon">
                          {getSortIcon('unitNoLegal')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('unitNoMarketing')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Marketing Unit
                        <span className="sort-icon">
                          {getSortIcon('unitNoMarketing')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('status')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Unit Status
                        <span className="sort-icon">
                          {getSortIcon('status')}
                        </span>
                      </button>
                    </th>
                    <th style={{ minWidth: '140px' }}>
                      <button
                        onClick={() => handleSort('dealId')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Deal
                        <span className="sort-icon">
                          {getSortIcon('dealId')}
                        </span>
                      </button>
                    </th>
                    <th style={{ minWidth: '130px' }}>
                      <button
                        onClick={() => handleSort('dealStage')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Deal Status
                        <span className="sort-icon">
                          {getSortIcon('dealStage')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('bed')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Beds
                        <span className="sort-icon">
                          {getSortIcon('bed')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('bath')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Baths
                        <span className="sort-icon">
                          {getSortIcon('bath')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('den')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Den
                        <span className="sort-icon">
                          {getSortIcon('den')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('media')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Media
                        <span className="sort-icon">
                          {getSortIcon('media')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('flex')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Flex
                        <span className="sort-icon">
                          {getSortIcon('flex')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('area')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Area(Sq.Ft)
                        <span className="sort-icon">
                          {getSortIcon('area')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('outdoorArea')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Outdoor (Sq.Ft)
                        <span className="sort-icon">
                          {getSortIcon('outdoorArea')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('ceiling')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Ceiling (Ft.)
                        <span className="sort-icon">
                          {getSortIcon('ceiling')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('allocateParking')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Parking
                        <span className="sort-icon">
                          {getSortIcon('allocateParking')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('allocateLocker')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Locker
                        <span className="sort-icon">
                          {getSortIcon('allocateLocker')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('allocateBicycle')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Bicycle
                        <span className="sort-icon">
                          {getSortIcon('allocateBicycle')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s', cursor: dataLoading ? 'wait' : 'default' }}>
                  {units.length === 0 ? (
                    <tr>
                      <td colSpan={25} className="data-table-empty">
                        No units found. Click "+ Add New Unit" to create one.
                      </td>
                    </tr>
                  ) : (
                    units.map((unit) => (
                      <tr
                        key={unit._id}
                        onClick={() => handleViewUnit(unit._id)}
                      >
                        <td>
                          <div style={{ width: '40px', height: '40px', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', overflow: 'hidden' }}>
                            {unit.primaryImageUrl ? (
                              <img
                                src={unit.primaryImageUrl}
                                alt={`Unit ${unit.unitNo}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Camera size={20} color="#9CA3AF" />
                            )}
                          </div>
                        </td>
                        <td className="data-table-primary-cell">{unit.projectName}</td>
                        <td>{unit.modelCollection || '-'}</td>
                        <td className="data-table-primary-cell">{unit.modelName}</td>
                        <td>{unit.type || '-'}</td>
                        <td style={{ fontWeight: '600' }}>{unit.unitNo}</td>
                        <td>{unit.salesPrice ? `$${unit.salesPrice.toLocaleString()}` : '-'}</td>
                        <td>{unit.floorLegal || '-'}</td>
                        <td>{unit.floorMarketing || '-'}</td>
                        <td>{unit.unitNoLegal || '-'}</td>
                        <td>{unit.unitNoMarketing || '-'}</td>
                        <td>
                          <span className={`data-table-status ${getStatusBadgeClass(unit.status)}`}>
                            {unit.status}
                          </span>
                        </td>
                        <td className="data-table-primary-cell">{unit.dealNumber || '-'}</td>
                        <td>
                          {unit.dealStatus ? (
                            <span className={`data-table-status ${getDealStatusBadgeClass(unit.dealStatus)}`}>
                              {unit.dealStatus}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{unit.bed || '-'}</td>
                        <td>{unit.bath || '-'}</td>
                        <td>{unit.den || '-'}</td>
                        <td>{unit.media || '-'}</td>
                        <td>{unit.flex || '-'}</td>
                        <td>{unit.area || '-'}</td>
                        <td>{unit.outdoorArea || '-'}</td>
                        <td>{unit.ceiling || '-'}</td>
                        <td>{unit.allocateParking || '-'}</td>
                        <td>{unit.allocateLocker || '-'}</td>
                        <td>{unit.allocateBicycle || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Pagination and Records Per Page */}
            <div className="contacts-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="contacts-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="contacts-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>

        {/* Drawer for Create/Edit */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title={editUnitId ? 'Edit Unit' : 'Add Unit'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseDrawer} form="unit-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="unit-form">
                {editUnitId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && (
            <UnitFormSlider
              workspaceId={workspaceId}
              unitId={editUnitId || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Unit</h3>
              <p>Are you sure you want to delete unit "{unitToDelete?.unitNo}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unit Details Slider */}
        {viewUnitId && workspaceId && (
          <UnitDetailsSlider
            workspaceId={workspaceId}
            unitId={viewUnitId}
            onClose={handleCloseDetailsSlider}
            onSuccess={fetchUnits}
          />
        )}

        {/* Unit Import Modal */}
        {showImportModal && workspaceId && filters.projectId && (
          <UnitImportModal
            isOpen={showImportModal}
            workspaceId={workspaceId}
            projectId={filters.projectId}
            builderId={projects.find(p => p._id === filters.projectId)?.builderId || ''}
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              setShowImportModal(false);
              fetchUnits();
            }}
          />
        )}

        {/* Project Selection Required Modal */}
        {showImportModal && (!filters.projectId) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Select Project</h2>
                <button
                  onClick={() => setShowImportModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6B7280',
                    padding: '0',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <p style={{ marginBottom: '16px', color: '#6B7280' }}>
                Please select a project before importing units.
              </p>
              <select
                className="form-input"
                value={filters.projectId || ''}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                style={{ width: '100%', marginBottom: '16px' }}
              >
                <option value="">Select a project...</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button className="btn btn-secondary btn-md" onClick={() => setShowImportModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </AppLayout>
  );
};

export default Units;
